import { CreativeOSFeatures, PlanConfig } from '@/_shared/subscriptionPlans'
import AccessControl from '@/components/AccessControl'
import { BoardDialog } from '@/components/BoardDialog.tsx'
import { Stack, Text } from '@/components/custom-components'
import CollectionCard from '@/components/custom-components/CollectionCard'
import { Loader } from '@/components/custom-components/Loader'
import { LockTagLargeNoBg, LockTagSmall } from '@/components/custom-components/LockTag'
import { ErrorDisplay } from '@/components/error.tsx'
import { Button } from '@/components/shadcn/button'
import useUserPermissions from '@/hooks/useUserPermissions'
import { trpc } from '@/utils/trpc.ts'
import { Lock, Plus } from '@mynaui/icons-react'
import { Link, createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'

type SearchParams = {
  sideBarOpen?: boolean
}

export const Route = createFileRoute('/feeds/boards/')({
  component: All,
  validateSearch: (search: Record<string, unknown>): SearchParams => {
    const sideBarOpen = search?.sideBarOpen as boolean

    return {
      sideBarOpen,
    }
  },
})

function All() {
  const [createBoardDialogOpen, setCreateBoardDialogOpen] = useState<boolean>(false)
  const { currentPlanConfigs } = useUserPermissions()

  const {
    data: myCollections,
    isLoading,
    error,
  } = trpc.getBoards.useQuery(
    { postType: 'record' },
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  )

  if (error) {
    return <ErrorDisplay />
  }

  if (isLoading) {
    return (
      <div className={'flex justify-center items-center w-full h-screen m-auto'}>
        <Loader />
      </div>
    )
  }

  return (
    <>
      {createBoardDialogOpen && (
        <BoardDialog open={createBoardDialogOpen} onOpenChange={() => setCreateBoardDialogOpen(false)} />
      )}
      <Stack className="gap-3 lg:gap-6">
        <div className={'flex justify-between lg:justify-start gap-5 items-center'}>
          <Text weight="semibold" size={'xxl'}>
            Your Collections
          </Text>

          <AccessControl
            feature={CreativeOSFeatures.ADD_NEW_COLLECTION}
            FeatureUnlocked={
              <Button
                onClick={() => setCreateBoardDialogOpen(true)}
                size={'sm'}
                variant={'default'}
                className="hidden lg:flex"
              >
                New Collection <Plus className="w-5 h-5" />
              </Button>
            }
            FeatureLocked={
              <Button size={'sm'} variant={'default'} className="hidden lg:flex justify-between opacity-50">
                <span>New Collection</span> <LockTagSmall />
              </Button>
            }
          />
        </div>

        {myCollections && currentPlanConfigs.length > 0 && (
          <BoardCollectionGridView
            boards={myCollections}
            currentPlanConfigs={currentPlanConfigs}
            createBoardDialogOpen={() => setCreateBoardDialogOpen(true)}
          />
        )}
      </Stack>
    </>
  )
}

type BoardCollectionDataProps = {
  boards: BoardDataProps[]
  createBoardDialogOpen: () => void
  currentPlanConfigs: PlanConfig[]
}

type BoardDataProps = {
  name: string
  id: string
  description: string | null
  isAdInBoard?: boolean
}

const BoardCollectionGridView = (props: BoardCollectionDataProps) => {
  const { currentPlanConfigs } = props
  return (
    <div className={'grid lg:grid-cols-2 gap-5 px-0 pb-10'}>
      {...[
        ...((props.boards ?? [])
          .slice(
            0,
            currentPlanConfigs.length > 0
              ? currentPlanConfigs[0].myCollectionLimit == 'unlimited'
                ? (props.boards ?? []).length
                : currentPlanConfigs[0].myCollectionLimit
              : 0,
          )
          .map((item) => <BoardCollectionItem data={item} key={item.id} />) || []),
        ...((props.boards ?? [])
          .slice(
            currentPlanConfigs.length > 0
              ? currentPlanConfigs[0].myCollectionLimit == 'unlimited'
                ? (props.boards ?? []).length
                : currentPlanConfigs[0].myCollectionLimit
              : 0,
            (props.boards ?? []).length,
          )
          .map((item) => (
            <AccessControl
              feature={CreativeOSFeatures.LOCKED}
              FeatureLocked={
                <div className="relative">
                  <BoardCollectionItem data={item} key={item.id} blur />

                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="bg-black bg-opacity-50 rounded-full p-0.5">
                      <LockTagLargeNoBg />
                    </div>
                  </div>
                </div>
              }
            />
          )) || []),
        // ...props.boards.map((item) => {
        //   return <BoardCollectionItem data={item} key={item.id} />;
        // }),
        ...(props.boards.length > 3
          ? []
          : [
              <div className={'overflow-y-hidden cursor-pointer'}>
                <AccessControl
                  feature={CreativeOSFeatures.ADD_NEW_COLLECTION}
                  FeatureUnlocked={
                    <Stack
                      className={
                        'rounded-lg h-96 border-dashed border-2 border-themeborder items-center justify-center gap-2'
                      }
                      onClick={() => props.createBoardDialogOpen()}
                    >
                      <Button variant={'default'} size="icon" className="rounded-full bg-thememutedforeground">
                        <Plus></Plus>
                      </Button>
                      <Text className="text-thememutedforeground text-center" size="xxl" weight="normal">
                        Create New Collection
                      </Text>
                    </Stack>
                  }
                  FeatureLocked={
                    <Stack
                      className={
                        'opacity-50 rounded-lg h-96 border-dashed border-2 border-themeborder items-center justify-center gap-2'
                      }
                    >
                      <Button variant={'default'} size="icon" className="rounded-full bg-thememutedforeground">
                        <Lock />
                      </Button>
                      <Text className="text-thememutedforeground text-center" size="xxl" weight="normal">
                        Create New Collection
                      </Text>
                    </Stack>
                  }
                />
              </div>,
            ]),
      ]}
    </div>
  )
}

const BoardCollectionItem = ({
  data,
  blur,
}: {
  data: BoardDataProps
  blur?: boolean
}) => {
  const { data: boardDetails } = trpc.getBoardDetails.useQuery({
    boardId: data.id,
  })

  return (
    <Link
      to={`/feeds/boards/$boardID/ads`}
      params={{ boardID: data.id }}
      className={`${blur ? 'blur-sm pointer-events-none' : ''}`}
    >
      <Stack className="gap-3">
        <CollectionCard
          imageUrls={[
            ...(boardDetails?.ads?.map((ad) => ad.imageUrl) || []),
            ...(boardDetails?.adTemplates?.map((adTemplate) => adTemplate.imageUrl) || []),
            ...(boardDetails?.landers?.map((lander) => lander.desktopScreenshot || lander.mobileScreenshot) || []),
          ]}
          videoUrls={boardDetails?.ads.map((ad) => ad.videoUrl) ?? []}
        />
        <div className="flex justify-between items-center">
          <Stack className="gap-1 w-full">
            <h4 className={`truncate font-semibold text-sm leading-5`}>{boardDetails?.name}</h4>
          </Stack>
          <Text className="text-thememutedforeground w-fit text-nowrap" size={'xs'} weight={'normal'}>
            {
              [...(boardDetails?.ads || []), ...(boardDetails?.adTemplates || []), ...(boardDetails?.landers || [])]
                .length
            }{' '}
            {[...(boardDetails?.ads || []), ...(boardDetails?.adTemplates || []), ...(boardDetails?.landers || [])]
              .length > 1
              ? ' Items'
              : 'Item'}
          </Text>
        </div>
      </Stack>
    </Link>
  )
}
